import _ from "lodash";
import * as THREE from "three";
import * as CANNON from "cannon";
import {
  create_d4_materials,
  create_dice_materials,
  create_geom
} from "./utils";

import {
  default_scale,
  default_d4_labels,
  default_d20_labels,
  default_d100_labels,
  dice_mass
} from "./settings";

export const DIE_SHAPES = [`d4`, `d6`, `d8`, `d10`, `d12`, `d20`];
export const DIE_TYPES = [`d4`, `d6`, `d8`, `d10`, `d12`, `d20`, `d100`];

// Todo add caching for materials/dice - memoize via lodash
export const createVertices = {
  d4: () => [
    [1, 1, 1],
    [-1, -1, 1],
    [-1, 1, -1],
    [1, -1, -1]
  ],
  d6: () => [
    [-1, -1, -1],
    [1, -1, -1],
    [1, 1, -1],
    [-1, 1, -1],
    [-1, -1, 1],
    [1, -1, 1],
    [1, 1, 1],
    [-1, 1, 1]
  ],
  d8: () => [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1]
  ],
  d10: () => {
    const a = (Math.PI * 2) / 10,
      h = 0.105;
    let vertices = [];
    for (let i = 0, b = 0; i < 10; ++i, b += a)
      vertices.push([Math.cos(b), Math.sin(b), h * (i % 2 ? 1 : -1)]);
    vertices.push([0, 0, -1]);
    vertices.push([0, 0, 1]);
    return vertices;
  },
  d12: () => {
    const p = (1 + Math.sqrt(5)) / 2,
      q = 1 / p;
    return [
      [0, q, p],
      [0, q, -p],
      [0, -q, p],
      [0, -q, -p],
      [p, 0, q],
      [p, 0, -q],
      [-p, 0, q],
      [-p, 0, -q],
      [q, p, 0],
      [q, -p, 0],
      [-q, p, 0],
      [-q, -p, 0],
      [1, 1, 1],
      [1, 1, -1],
      [1, -1, 1],
      [1, -1, -1],
      [-1, 1, 1],
      [-1, 1, -1],
      [-1, -1, 1],
      [-1, -1, -1]
    ];
  },
  d20: () => {
    const t = (1 + Math.sqrt(5)) / 2;
    return [
      [-1, t, 0],
      [1, t, 0],
      [-1, -t, 0],
      [1, -t, 0],
      [0, -1, t],
      [0, 1, t],
      [0, -1, -t],
      [0, 1, -t],
      [t, 0, -1],
      [t, 0, 1],
      [-t, 0, -1],
      [-t, 0, 1]
    ];
  }
};

export const createFaces = {
  d4: () => [
    [1, 0, 2, 1],
    [0, 1, 3, 2],
    [0, 3, 2, 3],
    [1, 2, 3, 4]
  ],
  d6: () => [
    [0, 3, 2, 1, 1],
    [1, 2, 6, 5, 2],
    [0, 1, 5, 4, 3],
    [3, 7, 6, 2, 4],
    [0, 4, 7, 3, 5],
    [4, 5, 6, 7, 6]
  ],
  d8: () => [
    [0, 2, 4, 1],
    [0, 4, 3, 2],
    [0, 3, 5, 3],
    [0, 5, 2, 4],
    [1, 3, 4, 5],
    [1, 4, 2, 6],
    [1, 2, 5, 7],
    [1, 5, 3, 8]
  ],
  d10: () => [
    [5, 7, 11, 0],
    [4, 2, 10, 1],
    [1, 3, 11, 2],
    [0, 8, 10, 3],
    [7, 9, 11, 4],
    [8, 6, 10, 5],
    [9, 1, 11, 6],
    [2, 0, 10, 7],
    [3, 5, 11, 8],
    [6, 4, 10, 9],
    [1, 0, 2, -1],
    [1, 2, 3, -1],
    [3, 2, 4, -1],
    [3, 4, 5, -1],
    [5, 4, 6, -1],
    [5, 6, 7, -1],
    [7, 6, 8, -1],
    [7, 8, 9, -1],
    [9, 8, 0, -1],
    [9, 0, 1, -1]
  ],
  d12: () => [
    [2, 14, 4, 12, 0, 1],
    [15, 9, 11, 19, 3, 2],
    [16, 10, 17, 7, 6, 3],
    [6, 7, 19, 11, 18, 4],
    [6, 18, 2, 0, 16, 5],
    [18, 11, 9, 14, 2, 6],
    [1, 17, 10, 8, 13, 7],
    [1, 13, 5, 15, 3, 8],
    [13, 8, 12, 4, 5, 9],
    [5, 4, 14, 9, 15, 10],
    [0, 12, 8, 10, 16, 11],
    [3, 19, 7, 17, 1, 12]
  ],
  d20: () => [
    [0, 11, 5, 1],
    [0, 5, 1, 2],
    [0, 1, 7, 3],
    [0, 7, 10, 4],
    [0, 10, 11, 5],
    [1, 5, 9, 6],
    [5, 11, 4, 7],
    [11, 10, 2, 8],
    [10, 7, 6, 9],
    [7, 1, 8, 10],
    [3, 9, 4, 11],
    [3, 4, 2, 12],
    [3, 2, 6, 13],
    [3, 6, 8, 14],
    [3, 8, 9, 15],
    [4, 9, 5, 16],
    [2, 4, 11, 17],
    [6, 2, 10, 18],
    [8, 6, 7, 19],
    [9, 8, 1, 20]
  ]
};

let DIE_GEOMETRY = {
  d4: {
    tab: -0.1,
    af: (Math.PI * 7) / 6,
    chamfer: 0.96,
    scaleFactor: 1.1
  },
  d6: {
    tab: 0.1,
    af: Math.PI / 4,
    chamfer: 0.96,
    scaleFactor: 0.9
  },
  d8: {
    tab: 0,
    af: -Math.PI / 8,
    chamfer: 0.965,
    scaleFactor: 1
  },
  d10: {
    tab: 0,
    af: (Math.PI * 6) / 5,
    chamfer: 0.945,
    scaleFactor: 0.9
  },
  d12: {
    tab: 0.2,
    af: -Math.PI / 8,
    chamfer: 0.968,
    scaleFactor: 0.9
  },
  d20: {
    tab: -0.2,
    af: -Math.PI / 8,
    chamfer: 0.955,
    scaleFactor: 1
  }
};

_.forEach(DIE_GEOMETRY, (geometry, dieShape) => {
  _.set(geometry, `vertices`, createVertices[dieShape]());
  _.set(geometry, `faces`, createFaces[dieShape]());
});

export { DIE_GEOMETRY };

export const DIE_MATERIAL_GENERATION = {
  d4: {
    materialFn: create_d4_materials,
    labels: default_d4_labels[0],
    sizeRatio: 1 / 2,
    marginRatio: 2
  },
  d6: {
    materialFn: create_dice_materials,
    labels: default_d20_labels,
    sizeRatio: 1 / 2,
    marginValue: 1
  },
  d8: {
    materialFn: create_dice_materials,
    labels: default_d20_labels,
    sizeRatio: 1 / 2,
    marginValue: 1.2
  },
  d10: {
    materialFn: create_dice_materials,
    labels: default_d20_labels,
    sizeRatio: 1 / 2,
    marginValue: 1
  },
  d12: {
    materialFn: create_dice_materials,
    labels: default_d20_labels,
    sizeRatio: 1 / 2,
    marginValue: 1
  },
  d20: {
    materialFn: create_dice_materials,
    labels: default_d20_labels,
    sizeRatio: 1 / 2,
    marginValue: 1
  },
  d100: {
    materialFn: create_dice_materials,
    labels: default_d100_labels,
    sizeRatio: 1 / 2,
    marginValue: 1.5
  }
};

export const createDieGeometry = (dieType, radius) => {
  if (!_.includes(DIE_SHAPES, dieType)) return null;
  if (!_.isFinite(radius)) return null;
  if (_.isSymbol(radius)) return null;
  const { vertices, faces, tab, af, chamfer, scaleFactor } = DIE_GEOMETRY[
    dieType
  ];
  return create_geom(vertices, faces, radius * scaleFactor, tab, af, chamfer);
};

export const createDieMaterials = (
  dieType,
  scale = default_scale,
  diceColor,
  labelColor
) => {
  if (!_.includes(DIE_TYPES, dieType)) return null;
  if (!_.isFinite(scale)) return null;
  if (_.isSymbol(scale)) return null;
  const {
    materialFn,
    labels,
    sizeRatio,
    marginRatio,
    marginValue
  } = DIE_MATERIAL_GENERATION[dieType];
  return materialFn(
    labels,
    scale * sizeRatio,
    marginRatio ? scale * marginRatio : marginValue,
    labelColor,
    diceColor
  );
};

export const createDie = (
  dieShape,
  dieType,
  scale = default_scale,
  diceColor,
  labelColor
) => {
  if (!_.includes(DIE_SHAPES, dieShape) || !_.includes(DIE_TYPES, dieType))
    return null;
  if (!_.isFinite(scale)) return null;
  if (_.isSymbol(scale)) return null;
  const geometry = createDieGeometry(dieShape, scale);
  const material = createDieMaterials(dieType, scale, diceColor, labelColor);
  return new THREE.Mesh(geometry, material);
};

export const createDice = {
  d4: _.partial(createDie, `d4`, `d4`),
  d6: _.partial(createDie, `d6`, `d6`),
  d8: _.partial(createDie, `d8`, `d8`),
  d10: _.partial(createDie, `d10`, `d10`),
  d12: _.partial(createDie, `d12`, `d12`),
  d20: _.partial(createDie, `d20`, `d20`),
  d100: _.partial(createDie, `d10`, `d100`)
};

export const createDieWithPhysics = (
  { type, pos, velocity, angle, axis },
  { scale, dice_body_material, diceColor, labelColor }
) => {
  let die = createDice[type](scale, diceColor, labelColor);
  die.castShadow = true;
  die.dieType = type;
  die.body = new CANNON.Body({
    mass: dice_mass[type],
    shape: die.geometry.cannon_shape,
    material: dice_body_material,
    type: CANNON.Body.DYNAMIC
  });
  die.body.position.set(pos.x, pos.y, pos.z);
  die.body.quaternion.setFromAxisAngle(
    new CANNON.Vec3(axis.x, axis.y, axis.z),
    axis.a * Math.PI * 2
  );
  die.body.angularVelocity.set(angle.x, angle.y, angle.z);
  die.body.velocity.set(velocity.x, velocity.y, velocity.z);
  die.body.linearDamping = 0.1;
  die.body.angularDamping = 0.1;

  return die;
};

export default createDice;
